import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchStates, fetchCountiesByState, fetchCitiesByCounty } from '../../services/api';
import RefreshDataButton from './RefreshDataButton';
import { useRole } from '../../contexts/RoleContext';
import { ROLES } from '../../contexts/RoleContext';

const FilterPanel = ({ onFilterChange }) => {
  const [filters, setFilters] = useState({
    state: { value: 'NJ', label: 'NJ' }, // Default to New Jersey
    county: null,
    city: null,
    startDate: null,
    endDate: null,
    hasUpsetValue: false,
  });
  const { userRole } = useRole();

  const [options, setOptions] = useState({
    states: [],
    counties: [],
    cities: [],
  });

  useEffect(() => {
    // Load states on component mount and apply default NJ filter
    const loadStates = async () => {
      try {
        const states = await fetchStates();
        setOptions(prev => ({
          ...prev,
          states: states.map(state => ({ value: state, label: state }))
        }));
        
        // Apply default NJ filter on mount
        const defaultFilters = {
          state: 'NJ',
          county: undefined,
          city: undefined,
          startDate: undefined,
          endDate: undefined,
          hasUpsetValue: undefined,
        };
        onFilterChange(defaultFilters);
      } catch (error) {
        console.error('Failed to load states:', error);
      }
    };

    loadStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove onFilterChange dependency to prevent infinite loops

  useEffect(() => {
    // Load counties when state changes
    const loadCounties = async () => {
      if (!filters.state) {
        setOptions(prev => ({ ...prev, counties: [], cities: [] }));
        return;
      }

      try {
        const counties = await fetchCountiesByState(filters.state.value);
        setOptions(prev => ({
          ...prev,
          counties: counties.map(county => ({ value: county, label: county })),
          cities: []
        }));
        
        // Reset county and city when state changes
        setFilters(prev => ({ ...prev, county: null, city: null }));
      } catch (error) {
        console.error('Failed to load counties:', error);
      }
    };

    loadCounties();
  }, [filters.state]);

  useEffect(() => {
    // Load cities when county changes
    const loadCities = async () => {
      if (!filters.county) {
        setOptions(prev => ({ ...prev, cities: [] }));
        return;
      }

      try {
        const cities = await fetchCitiesByCounty(filters.county.value);
        setOptions(prev => ({
          ...prev,
          cities: cities.map(city => ({ value: city, label: city }))
        }));
        
        // Reset city when county changes
        setFilters(prev => ({ ...prev, city: null }));
      } catch (error) {
        console.error('Failed to load cities:', error);
      }
    };

    loadCities();
  }, [filters.county]);

  const handleApplyFilters = () => {
    // Convert filters to the format expected by the API
    const apiFilters = {
      state: filters.state?.value || undefined,
      county: filters.county?.value || undefined,
      city: filters.city?.value || undefined,
      startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
      endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
      hasUpsetValue: filters.hasUpsetValue || undefined,
    };

    onFilterChange(apiFilters);
  };

  const handleReset = () => {
    setFilters({
      state: { value: 'NJ', label: 'NJ' }, // Reset to default NJ
      county: null,
      city: null,
      startDate: null,
      endDate: null,
      hasUpsetValue: false,
    });
    
    onFilterChange({
      state: 'NJ',
      county: undefined,
      city: undefined,
      startDate: undefined,
      endDate: undefined,
      hasUpsetValue: undefined,
    });
  };

  const handleRefreshComplete = () => {
    // Reset filters and trigger a new search
    handleReset();
  };

  return (
    <div className="card bg-[#ffffff] shadow-lg rounded-lg p-6 mb-6 border-2 border-[#96ffa8]">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-black flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-[#96ffa8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filter Foreclosures
        </h2>
        {userRole === ROLES.ADMIN && <RefreshDataButton onRefreshComplete={handleRefreshComplete} />}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* State Filter */}
        <div>
          <label className="block text-sm font-medium text-black mb-1" htmlFor="state">State</label>
          <Select
            id="state"
            isClearable
            placeholder="Select State"
            options={options.states}
            value={filters.state}
            onChange={(selected) => setFilters(prev => ({ ...prev, state: selected }))}
            className="w-full"
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: '#96ffa8',
                primary25: '#e6ffea',
              },
            })}
          />
        </div>
        
        {/* County Filter */}
        <div>
          <label className="block text-sm font-medium text-black mb-1" htmlFor="county">County</label>
          <Select
            id="county"
            isClearable
            placeholder="Select County"
            options={options.counties}
            value={filters.county}
            onChange={(selected) => setFilters(prev => ({ ...prev, county: selected }))}
            isDisabled={!filters.state}
            className="w-full"
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: '#96ffa8',
                primary25: '#e6ffea',
              },
            })}
          />
        </div>
        
        {/* City Filter */}
        <div>
          <label className="block text-sm font-medium text-black mb-1" htmlFor="city">City</label>
          <Select
            id="city"
            isClearable
            placeholder="Select City"
            options={options.cities}
            value={filters.city}
            onChange={(selected) => setFilters(prev => ({ ...prev, city: selected }))}
            isDisabled={!filters.county}
            className="w-full"
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: '#96ffa8',
                primary25: '#e6ffea',
              },
            })}
          />
        </div>
      </div>
      
      {/* Date Range and Upset Value Filters */}
      <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6">
        <div className="w-full md:w-1/4">
          <label className="block text-sm font-medium text-black mb-1" htmlFor="startDate">From Date</label>
          <DatePicker
            id="startDate"
            selected={filters.startDate}
            onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
            selectsStart
            startDate={filters.startDate}
            endDate={filters.endDate}
            placeholderText="Select start date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#96ffa8] focus:border-[#96ffa8]"
          />
        </div>
        
        <div className="w-full md:w-1/4">
          <label className="block text-sm font-medium text-black mb-1" htmlFor="endDate">To Date</label>
          <DatePicker
            id="endDate"
            selected={filters.endDate}
            onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
            selectsEnd
            startDate={filters.startDate}
            endDate={filters.endDate}
            minDate={filters.startDate}
            placeholderText="Select end date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#96ffa8] focus:border-[#96ffa8]"
          />
        </div>

        {/* Upset Value Filter */}
        <div className="flex items-center md:ml-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.hasUpsetValue}
              onChange={(e) => setFilters(prev => ({ ...prev, hasUpsetValue: e.target.checked }))}
              className="form-checkbox h-4 w-4 text-[#96ffa8] rounded border-gray-300 focus:ring-[#96ffa8]"
            />
            <span className="text-sm font-medium text-gray-700">Show only listings with upset value</span>
          </label>
        </div>
        
        <div className="flex justify-end space-x-3 md:ml-auto">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#96ffa8] focus:border-[#96ffa8]"
          >
            Reset
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel; 